import { useState, useEffect, useCallback, useMemo } from 'react';
import { Trash2, ChevronRight, FileUp } from 'lucide-react';
import { rpc } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Pagination } from '@/components/common/Pagination';
import { RaceSessionDetailDialog } from '@/components/races/RaceSessionDetailDialog';
import { FilterBar } from '@shared/search';
import { racesSchema } from './racesSchema';
import { ImportRacePDFsDialog } from '@/components/races/ImportRacePDFsDialog';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';

interface RaceSessionItem {
  id: string;
  date: string;
  time: string;
  sessionType: string;
  sessionLabel: string;
  trackName: string;
  country: string;
  championshipName: string;
  categoryName: string;
  entryCount: number;
  totalLaps: number;
}

interface EventGroup {
  key: string;
  date: string;
  trackName: string;
  country: string;
  championshipName: string;
  sessions: RaceSessionItem[];
}

interface ListResponse {
  sessions: RaceSessionItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

function getHashParams(): URLSearchParams {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  return new URLSearchParams(qIdx >= 0 ? hash.slice(qIdx) : '');
}

function updateHashParams(page: number, query: string) {
  const hash = window.location.hash;
  const base = hash.includes('?') ? hash.slice(0, hash.indexOf('?')) : hash;
  const sp = new URLSearchParams();
  if (page > 1) sp.set('page', String(page));
  if (query) sp.set('q', query);
  const qs = sp.toString();
  window.history.replaceState(null, '', qs ? `${base}?${qs}` : base);
}

function groupByEvent(sessions: RaceSessionItem[]): EventGroup[] {
  const map = new Map<string, EventGroup>();
  for (const s of sessions) {
    const key = `${s.date}::${s.trackName}`;
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        date: s.date,
        trackName: s.trackName,
        country: s.country,
        championshipName: s.championshipName,
        sessions: [],
      };
      map.set(key, group);
    }
    group.sessions.push(s);
  }
  return Array.from(map.values());
}

const PAGE_SIZE = 10;

/* Grid templates */
const EVENT_GRID = 'grid-cols-[24px_110px_1fr_60px_100px]';
const SESSION_GRID = 'grid-cols-[24px_60px_1fr_100px_100px_60px_60px_32px]';

export function RacesTab() {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [sessions, setSessions] = useState<RaceSessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(() => {
    const p = parseInt(getHashParams().get('page') || '1');
    return p > 0 ? p : 1;
  });
  const [query, setQuery] = useState(() => getHashParams().get('q') || '');
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [importOpen, setImportOpen] = useState(false);

  // Expand/collapse state (set of event keys)
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Detail dialog state
  const [selectedSession, setSelectedSession] = useState<RaceSessionItem | null>(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<RaceSessionItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadSessions = useCallback(async (p: number, q: string) => {
    setLoading(true);
    try {
      const body: Record<string, unknown> = { page: p, pageSize: PAGE_SIZE };
      if (q) body.query = q;
      const data = await rpc<ListResponse>('ListEditorRaceSessions', body);
      setSessions(data.sessions ?? []);
      setTotalCount(data.totalCount ?? 0);
      setTotalPages(Math.max(1, Math.ceil((data.totalCount ?? 0) / (data.pageSize ?? PAGE_SIZE))));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions(page, query);
  }, [page, query, loadSessions]);

  useEffect(() => {
    updateHashParams(page, query);
  }, [page, query]);

  useEffect(() => {
    const handleHashSync = () => {
      const p = parseInt(getHashParams().get('page') || '1');
      const q = getHashParams().get('q') || '';
      setPage(p > 0 ? p : 1);
      setQuery(q);
    };
    window.addEventListener('hashchange', handleHashSync);
    window.addEventListener('popstate', handleHashSync);
    return () => {
      window.removeEventListener('hashchange', handleHashSync);
      window.removeEventListener('popstate', handleHashSync);
    };
  }, []);

  // Reset expanded state when page/query changes
  useEffect(() => {
    setExpanded(new Set());
  }, [page, query]);

  const eventGroups = useMemo(() => groupByEvent(sessions), [sessions]);

  const toggleEvent = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSearch = (newQuery: string) => {
    setPage(1);
    setQuery(newQuery);
  };

  const handleDeleteSession = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await rpc('DeleteRaceSession', { id: deleteTarget.id });
      showToast(t('races.deleted'));
      setDeleteTarget(null);
      loadSessions(page, query);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-[1.4rem] font-bold text-text-primary tracking-tight">{t('races.title')}</h2>
        <button
          onClick={() => setImportOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
            bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer"
        >
          <FileUp size={14} />
          <span className="hidden sm:inline">{t('import.importButton')}</span>
        </button>
      </div>

      {/* Filter Toolbar */}
      <div className="mb-6">
        <FilterBar
          schema={racesSchema}
          initialQuery={query}
          onSearch={handleSearch}
          enableFilterUI={true}
        />
      </div>

      {/* Results */}
      {loading ? (
        <div className="card overflow-hidden">
          <LoadingSpinner message={t('races.loading')} />
        </div>
      ) : error ? (
        <ErrorState message={t('races.loadFailed', { error })} />
      ) : sessions.length === 0 ? (
        <div className="card">
          <EmptyState icon="🏁" message={t('races.empty')} />
        </div>
      ) : (
        <div className="animate-fade-in">
          <div className="card overflow-hidden">
            {/* Desktop: event-level header */}
            <div className={`hidden sm:grid ${EVENT_GRID} gap-3 px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted divider`}>
              <span></span>
              <span>{t('races.date')}</span>
              <span>{t('races.track')}</span>
              <span>{t('common.year')}</span>
              <span>{t('races.sessionType')}</span>
            </div>

            {/* Desktop grouped rows */}
            {eventGroups.map((group, gi) => {
              const isExpanded = expanded.has(group.key);
              const year = group.date.slice(0, 4);
              const sessionCount = group.sessions.length;
              const sessionCountLabel = sessionCount === 1
                ? t('races.sessionCountSingular')
                : t('races.sessionCount', { count: sessionCount });

              return (
                <div key={group.key}>
                  {/* Event parent row (desktop) */}
                  <div
                    onClick={() => toggleEvent(group.key)}
                    className={`hidden sm:grid ${EVENT_GRID} gap-3 px-4 py-3.5
                      cursor-pointer hover:bg-bg-hover active:scale-[0.998]
                      transition-all select-none items-center
                      ${!isExpanded && gi < eventGroups.length - 1 ? 'divider' : ''}`}
                  >
                    <ChevronRight
                      size={14}
                      className={`text-text-muted transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                    />
                    <span className="text-text-secondary text-xs tabular-nums mono">
                      {group.date}
                    </span>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold text-text-primary text-sm truncate">{group.trackName}</span>
                      {group.country && (
                        <span className="text-text-muted text-xs shrink-0">{group.country}</span>
                      )}
                      {group.championshipName && (
                        <span className="text-text-muted text-xs truncate">· {group.championshipName}</span>
                      )}
                    </div>
                    <span>
                      <span className="inline-block px-2 py-0.5 bg-white/[0.06] border border-border-subtle rounded-full text-[0.7rem] font-medium text-text-secondary tabular-nums">
                        {year}
                      </span>
                    </span>
                    <span className="text-text-muted text-xs">
                      {sessionCountLabel}
                    </span>
                  </div>

                  {/* Expanded: session sub-header + session rows (desktop) */}
                  {isExpanded && (
                    <>
                      {/* Session column header */}
                      <div className={`hidden sm:grid ${SESSION_GRID} gap-3 px-4 py-2 bg-bg-surface/50
                        text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted border-t border-border-subtle`}
                      >
                        <span></span>
                        <span>{t('races.date')}</span>
                        <span>{t('races.track')}</span>
                        <span>{t('races.sessionType')}</span>
                        <span>{t('races.category')}</span>
                        <span className="text-center">{t('races.entries')}</span>
                        <span className="text-center">{t('races.laps')}</span>
                        <span></span>
                      </div>

                      {/* Session rows */}
                      {group.sessions.map((s, si) => (
                        <div
                          key={s.id}
                          onClick={() => setSelectedSession(s)}
                          className={`
                            hidden sm:grid ${SESSION_GRID} gap-3
                            px-4 py-3 cursor-pointer
                            hover:bg-bg-hover active:scale-[0.995]
                            transition-all group bg-bg-surface/50
                            ${si < group.sessions.length - 1 ? 'border-b border-border-subtle' : ''}
                            ${si === group.sessions.length - 1 && gi < eventGroups.length - 1 ? 'divider' : ''}
                          `}
                        >
                          <span className="flex items-center justify-center">
                            <span className="w-1 h-1 rounded-full bg-border-default"></span>
                          </span>
                          <span className="text-text-muted text-xs tabular-nums mono">
                            {s.time || '—'}
                          </span>
                          <span className="text-text-secondary text-sm truncate">{s.trackName}</span>
                          <span>
                            <span className="inline-block px-2.5 py-0.5 bg-accent-subtle border border-accent/20 rounded-full text-xs font-medium text-accent-hover tracking-wide">
                              {s.sessionLabel || s.sessionType}
                            </span>
                          </span>
                          <span className="text-text-secondary text-sm truncate">{s.categoryName}</span>
                          <span className="text-text-muted text-sm tabular-nums text-center">{s.entryCount}</span>
                          <span className="text-text-muted text-sm tabular-nums text-center">{s.totalLaps}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}
                            title={t('races.deleteConfirmTitle')}
                            aria-label={`${t('races.deleteConfirmTitle')} ${s.trackName} ${s.date}`}
                            className="p-1 rounded text-text-muted
                              hover:text-red hover:bg-red/10 transition-all cursor-pointer justify-self-center"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Event parent row (mobile) */}
                  <div
                    onClick={() => toggleEvent(group.key)}
                    className={`sm:hidden px-4 py-3.5 cursor-pointer hover:bg-bg-hover
                      transition-colors active:scale-[0.998] select-none
                      ${!isExpanded && gi < eventGroups.length - 1 ? 'border-b border-border-default' : ''}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <ChevronRight
                        size={14}
                        className={`text-text-muted transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-semibold text-text-primary truncate">{group.trackName}</span>
                            <span className="inline-block px-2 py-0.5 bg-white/[0.06] border border-border-subtle rounded-full text-[0.65rem] font-medium text-text-secondary tabular-nums shrink-0">
                              {year}
                            </span>
                          </div>
                          <span className="text-[0.7rem] text-text-muted shrink-0">{sessionCountLabel}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[0.7rem] text-text-muted mono">{group.date}</span>
                          {group.country && (
                            <>
                              <span className="text-[0.7rem] text-text-secondary">·</span>
                              <span className="text-[0.7rem] text-text-muted">{group.country}</span>
                            </>
                          )}
                          {group.championshipName && (
                            <>
                              <span className="text-[0.7rem] text-text-secondary">·</span>
                              <span className="text-[0.7rem] text-text-secondary truncate">{group.championshipName}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded child session rows (mobile) */}
                  {isExpanded && group.sessions.map((s, si) => (
                    <button
                      key={s.id}
                      onClick={() => setSelectedSession(s)}
                      className={`sm:hidden w-full text-left pl-10 pr-4 py-3 hover:bg-bg-hover
                        transition-colors group border-t border-border-subtle bg-bg-surface/50
                        ${si === group.sessions.length - 1 && gi < eventGroups.length - 1 ? 'border-b border-border-default' : ''}`}
                    >
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-1 h-1 rounded-full bg-border-default shrink-0"></span>
                            <span className="inline-block px-2.5 py-0.5 bg-accent-subtle border border-accent/20 rounded-full text-xs font-medium text-accent-hover tracking-wide shrink-0">
                              {s.sessionLabel || s.sessionType}
                            </span>
                            <span className="text-[0.7rem] text-text-muted mono shrink-0">{s.time || '—'}</span>
                          </div>
                          <div
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}
                            className="p-1 text-text-muted hover:text-red"
                          >
                            <Trash2 size={14} />
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap pl-3">
                          <span className="text-[0.7rem] text-text-secondary truncate">{s.categoryName}</span>
                          <span className="text-[0.7rem] text-text-muted ml-auto">{s.entryCount} {t('races.entries').toLowerCase()} · {s.totalLaps} {t('races.laps').toLowerCase()}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Race Session Detail Dialog */}
      {selectedSession && (
        <RaceSessionDetailDialog
          session={{
            id: selectedSession.id,
            sessionLabel: selectedSession.sessionLabel || selectedSession.sessionType,
            trackName: selectedSession.trackName,
            date: selectedSession.date,
          }}
          onClose={() => setSelectedSession(null)}
          onSaved={() => loadSessions(page, query)}
        />
      )}

      {/* Import Race PDFs Dialog */}
      {importOpen && (
        <ImportRacePDFsDialog
          onClose={() => setImportOpen(false)}
          onComplete={() => { setImportOpen(false); loadSessions(page, query); }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        onConfirm={handleDeleteSession}
        onCancel={() => setDeleteTarget(null)}
        title={t('races.deleteConfirmTitle')}
        message={deleteTarget ? (
          <span dangerouslySetInnerHTML={{ __html: t('races.deleteConfirmMessage', { type: deleteTarget.sessionLabel || deleteTarget.sessionType, track: deleteTarget.trackName, date: deleteTarget.date }) }} />
        ) : ''}
        confirmLabel={t('races.deleteConfirmButton')}
        loading={deleting}
      />
    </div>
  );
}
